<template>
  <div class="ca-promo-content-card" :class="`ca-promo-content-card--${theme}`">
    <span
      v-if="isClear && (iconSrc || iconName)"
      class="ca-promo-content-card__avatar"
      aria-hidden="true"
    >
      <img
        v-if="iconSrc"
        class="ca-promo-content-card__icon-image"
        :src="iconSrc"
        alt=""
      />
      <IconComponent
        v-else-if="iconName"
        :name="iconName"
        size="4"
        icon-class="ca-promo-content-card__ds-icon"
      />
    </span>

    <component
      v-if="title"
      :is="normalizeHeadingTag(titleHeadingType, 'p')"
      class="ca-promo-content-card__title heading-large"
    >
      {{ title }}
    </component>
    <component
      v-if="description"
      :is="normalizeHeadingTag(descriptionHeadingType, 'p')"
      class="ca-promo-content-card__description caption-action"
    >
      {{ description }}
    </component>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { IconComponent } from "ca-online-design-system";
import { normalizeHeadingTag } from "../../../utils/heading";

defineOptions({
  name: "CaPromoContentCard",
});

const props = defineProps({
  title: {
    type: String,
    default: "",
  },
  titleHeadingType: {
    type: String,
    default: "Paragraph",
  },
  description: {
    type: String,
    default: "",
  },
  descriptionHeadingType: {
    type: String,
    default: "Paragraph",
  },
  theme: {
    type: String,
    default: "light-green",
    validator: (value) =>
      ["light-green", "dark-green", "clear"].includes(value),
  },
  iconSrc: {
    type: String,
    default: "",
  },
  iconName: {
    type: String,
    default: "",
  },
});

const isClear = computed(() => props.theme === "clear");
</script>

<style scoped lang="scss">
.ca-promo-content-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--size-2);
  width: 100%;
  height: 100%;
  min-width: 0;
  padding: var(--size-5) var(--size-4) !important;
  border-radius: var(--size-4);
  color: var(--ca-color-text-body);
  text-align: center;
  overflow-wrap: break-word;
  word-break: normal;
  background-color: var(--ca-color-backgrounds-container-high);

  @include ca-bp("lg") {
    padding: var(--size-5) var(--size-4);
  }
  @include ca-bp("md") {
    border-radius: var(--size-5);
  }
}

.ca-promo-content-card--dark-green {
  padding: var(--size-4);
  color: var(--ca-color-text-inverted);
  background-color: var(--ca-color-theme-primary);

  @include ca-bp("sm") {
    padding: var(--size-5) var(--size-4);
  }
}

.ca-promo-content-card--clear {
  justify-content: flex-start;
  gap: var(--size-3);
  padding: var(--size-3);
  background-color: var(--ca-color-neutrals-inverted-80);
  backdrop-filter: blur(24px);

  @include ca-bp("sm") {
    gap: var(--size-4);
    padding: var(--size-5) var(--size-4);
  }
}

.ca-promo-content-card__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  color: var(--ca-color-icons-primary);
  background-color: var(--ca-color-neutrals-inverted);

  @include ca-bp("sm") {
    width: 56px;
    height: 56px;
    flex-basis: 56px;
  }

  @include ca-bp("lg") {
    width: 56px;
    height: 56px;
    flex-basis: 56px;
  }
}

.ca-promo-content-card__icon-image {
  display: block;
  width: 24px;
  height: 24px;
  object-fit: contain;

  @include ca-bp("sm") {
    width: 32px;
    height: 32px;
  }
}

:deep(.ca-promo-content-card__ds-icon) {
  width: 16px;
  height: 16px;
}

:deep(.ca-promo-content-card__ds-icon svg) {
  display: block;
  width: 100%;
  height: 100%;
}

@include ca-bp("sm") {
  :deep(.ca-promo-content-card__ds-icon) {
    width: 24px;
    height: 24px;
  }
}

@include ca-bp("lg") {
  :deep(.ca-promo-content-card__ds-icon) {
    width: 32px;
    height: 32px;
  }
}

.ca-promo-content-card__title,
.ca-promo-content-card__description {
  width: 100%;
  margin: 0;
  color: inherit;
  word-break: normal;
}

@media (min-width: 768px) and (max-width: 1023px) {
  .ca-promo-content-card__title {
    font-size: 32px;
    font-weight: 700;
  }
}

@media (max-width: 767px) {
  .ca-promo-content-card__title {
    font-size: 24px !important;
    font-weight: 700 !important;
  }

  .ca-promo-content-card__description {
    font-size: 14px;
    font-weight: 500;
  }
}
</style>
